import { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import {
  ClipboardCheck, Loader2, AlertTriangle, CheckCircle2, XCircle,
  Shield, ChevronDown, ChevronRight, Bell, Eye, FileCheck
} from 'lucide-react';

const SEVERITY_COLORS = {
  high: 'text-rose-700 dark:text-rose-400 bg-rose-100 dark:bg-rose-950/30 border-rose-300 dark:border-rose-800/40',
  medium: 'text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800/40',
  low: 'text-sky-700 dark:text-sky-400 bg-sky-100 dark:bg-sky-950/30 border-sky-300 dark:border-sky-800/40',
};

const GRADE_COLORS = {
  A: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800',
  B: 'text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-950/30 border-cyan-200 dark:border-cyan-800',
  C: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800',
  D: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800',
  F: 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800',
};

export default function AuditPanel({ projectId }) {
  const [audits, setAudits] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [signoffLog, setSignoffLog] = useState([]);
  const [running, setRunning] = useState(false);
  const [runningHandover, setRunningHandover] = useState(false);
  const [expandedAudit, setExpandedAudit] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [auditsRes, alertsRes, signoffRes] = await Promise.all([
        api.get(`/closeout/projects/${projectId}/division-audits`),
        api.get(`/closeout/projects/${projectId}/smart-alerts`),
        api.get(`/closeout/projects/${projectId}/handover/signoff-log`),
      ]);
      setAudits(auditsRes.data);
      setAlerts(alertsRes.data);
      setSignoffLog(signoffRes.data.entries || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runAudit = async () => {
    setRunning(true);
    try {
      await api.post(`/closeout/projects/${projectId}/division-audit`);
      await fetchData();
    } catch (err) { console.error(err); }
    finally { setRunning(false); }
  };

  const runHandoverAudit = async () => {
    setRunningHandover(true);
    try {
      await api.post(`/closeout/projects/${projectId}/handover-audit`);
      const { toast } = await import('sonner');
      toast.success('Handover readiness audit complete');
      await fetchData();
    } catch (err) {
      console.error(err);
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Handover audit failed');
    }
    finally { setRunningHandover(false); }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await api.patch(`/closeout/projects/${projectId}/smart-alerts/${alertId}/acknowledge`);
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, acknowledged: true } : a));
    } catch (err) { console.error(err); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  );

  const activeAlerts = alerts.filter(a => !a.acknowledged);
  const acknowledgedAlerts = alerts.filter(a => a.acknowledged);

  return (
    <div className="space-y-8" data-testid="audit-panel">

      {/* Handover Sign-off Trail */}
      <section data-testid="handover-signoff-trail">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-heading uppercase tracking-widest text-primary/80 flex items-center gap-2">
            <FileCheck className="w-4 h-4" /> Handover Sign-off Trail
            {signoffLog.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[9px] font-mono rounded-sm bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/40">
                {signoffLog.length}
              </span>
            )}
          </h2>
        </div>
        {signoffLog.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-24 bg-card border border-dashed border-border rounded-sm">
            <FileCheck className="w-6 h-6 text-muted-foreground mb-2" />
            <p className="text-xs text-muted-foreground">No sign-offs yet — reviewers' approvals will appear here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {signoffLog.map(entry => (
              <div key={entry.id}
                data-testid={`signoff-entry-${entry.id}`}
                className={`flex items-start gap-3 p-3 rounded-sm border ${
                  entry.signed_off
                    ? 'border-emerald-300 dark:border-emerald-800/40 bg-emerald-50/60 dark:bg-emerald-950/20'
                    : 'border-amber-300 dark:border-amber-800/40 bg-amber-50/50 dark:bg-amber-950/20'
                }`}>
                {entry.signed_off
                  ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" />
                  : <XCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground">
                    {entry.signed_off ? 'Signed off' : 'Re-opened'}: {entry.section}
                  </p>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className="text-[10px] text-cyan-600 dark:text-cyan-400 flex items-center gap-1">
                      <Eye className="w-2.5 h-2.5" /> {entry.user_name || entry.user_email || 'Unknown'}
                    </span>
                    <span className="text-[10px] font-mono text-muted-foreground">
                      {entry.created_at ? new Date(entry.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Smart Alerts Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-heading uppercase tracking-widest text-primary/80 flex items-center gap-2">
            <Bell className="w-4 h-4" /> Smart Alerts
            {activeAlerts.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[9px] font-mono rounded-sm bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 border border-rose-300 dark:border-rose-800/40">
                {activeAlerts.length} ACTIVE
              </span>
            )}
          </h2>
        </div>

        {alerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-28 bg-card border border-dashed border-border rounded-sm">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 mb-2" />
            <p className="text-xs text-muted-foreground">No alerts — all clear</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activeAlerts.map(alert => (
              <div key={alert.id}
                data-testid={`alert-${alert.id}`}
                className={`flex items-start gap-3 p-3 rounded-sm border ${SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.medium}`}>
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium">{alert.message}</p>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-[9px] font-mono text-muted-foreground">
                      {alert.equipment_type} — {alert.location || 'No location'}
                    </span>
                    <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm bg-muted/50 text-muted-foreground">
                      {alert.severity}
                    </span>
                  </div>
                </div>
                <button onClick={() => acknowledgeAlert(alert.id)}
                  data-testid={`ack-alert-${alert.id}`}
                  className="shrink-0 text-[10px] font-mono text-muted-foreground hover:text-emerald-400 transition-colors px-2 py-1 border border-border rounded-sm hover:border-emerald-800">
                  Acknowledge
                </button>
              </div>
            ))}
            {acknowledgedAlerts.length > 0 && (
              <details className="mt-2">
                <summary className="text-[10px] text-muted-foreground cursor-pointer hover:text-muted-foreground">
                  {acknowledgedAlerts.length} acknowledged alert{acknowledgedAlerts.length > 1 ? 's' : ''}
                </summary>
                <div className="mt-2 space-y-1 opacity-50">
                  {acknowledgedAlerts.map(alert => (
                    <div key={alert.id} className="flex items-center gap-2 p-2 text-[10px] text-muted-foreground bg-muted/30 rounded-sm">
                      <CheckCircle2 className="w-3 h-3 text-emerald-700" />
                      <span className="truncate">{alert.message}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </section>

      {/* Division Audit Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-heading uppercase tracking-widest text-cyan-500/80 flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" /> Division Audits
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={runHandoverAudit} disabled={runningHandover}
              data-testid="run-handover-audit-btn"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors text-emerald-600 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/40 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 hover:border-emerald-400 dark:hover:border-emerald-700 disabled:opacity-50">
              {runningHandover ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileCheck className="w-3.5 h-3.5" />}
              {runningHandover ? 'Checking...' : 'Handover Check'}
            </button>
            <button onClick={runAudit} disabled={running}
              data-testid="run-audit-btn"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors text-cyan-600 dark:text-cyan-400 border border-cyan-300 dark:border-cyan-800/40 hover:bg-cyan-50 dark:hover:bg-cyan-950/30 hover:border-cyan-400 dark:hover:border-cyan-700 disabled:opacity-50">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardCheck className="w-3.5 h-3.5" />}
              {running ? 'Auditing...' : 'Division Audit'}
            </button>
          </div>
        </div>

        {audits.length === 0 && !running ? (
          <div className="flex flex-col items-center justify-center h-40 bg-card border border-dashed border-border rounded-sm">
            <ClipboardCheck className="w-8 h-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-1">No audits run yet</p>
            <p className="text-xs text-muted-foreground">Run an audit to check division completeness before handover</p>
          </div>
        ) : (
          <div className="space-y-3">
            {audits.map((audit, idx) => {
              const expanded = expandedAudit === audit.id;
              const isHandover = audit.type === 'handover';
              const readiness = audit.ai_report?.match(/(?:HANDOVER READINESS|OVERALL VERDICT)[:\s]*\*?\*?\s*(READY(?:\s+FOR\s+HANDOVER)?|CONDITIONALLY READY|NOT READY)/i);
              const readinessLabel = readiness ? readiness[1] : null;
              const readinessColor = readinessLabel?.includes('NOT') ? 'text-rose-600 dark:text-rose-400' :
                readinessLabel?.includes('CONDITIONALLY') ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400';
              const ReadinessIcon = readinessLabel?.includes('NOT') ? XCircle :
                readinessLabel?.includes('CONDITIONALLY') ? AlertTriangle : CheckCircle2;
              const scorePct = isHandover ? audit.overall_score_pct : audit.overall_completeness_pct;

              return (
                <div key={audit.id} className="bg-card border border-border rounded-sm overflow-hidden"
                  data-testid={`audit-${audit.id}`}>
                  <button onClick={() => setExpandedAudit(expanded ? null : audit.id)}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-secondary/20 transition-colors">
                    {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-foreground">
                          {isHandover ? 'Handover Check' : 'Division Audit'} #{audits.length - idx}
                        </span>
                        {isHandover && (
                          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                            HANDOVER
                          </span>
                        )}
                        {readinessLabel && (
                          <span className={`flex items-center gap-1 text-[10px] font-mono uppercase ${readinessColor}`}>
                            <ReadinessIcon className="w-3 h-3" /> {readinessLabel}
                          </span>
                        )}
                        <span className="text-[10px] font-mono text-muted-foreground">
                          {new Date(audit.generated_at).toLocaleDateString()} {new Date(audit.generated_at).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-[10px] text-muted-foreground">
                          {scorePct}% {isHandover ? 'ready' : 'complete'}
                        </span>
                        {!isHandover && (
                          <>
                            <span className="text-[10px] text-muted-foreground">{audit.critical_gap_count} critical gaps</span>
                            <span className="text-[10px] text-muted-foreground">{audit.active_assets} assets ({audit.verified_assets} verified)</span>
                          </>
                        )}
                        {isHandover && audit.summary && (
                          <>
                            <span className="text-[10px] text-muted-foreground">{audit.summary.open_rfis} open RFIs</span>
                            <span className="text-[10px] text-muted-foreground">{audit.summary.approved_submittals}/{audit.summary.total_submittals} submittals</span>
                            <span className="text-[10px] text-muted-foreground">{audit.summary.verified_assets}/{audit.summary.active_assets} assets verified</span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>

                  {expanded && (
                    <div className="border-t border-border p-5">
                      {/* Summary stats */}
                      {isHandover && audit.summary ? (
                        <div className="grid grid-cols-5 gap-2 mb-5">
                          {[
                            { label: 'Readiness', value: `${scorePct}%`, color: scorePct >= 80 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400' },
                            { label: 'Open RFIs', value: audit.summary.open_rfis, color: audit.summary.open_rfis === 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400' },
                            { label: 'Submittals', value: `${audit.summary.approved_submittals}/${audit.summary.total_submittals}`, color: 'text-cyan-600 dark:text-cyan-400' },
                            { label: 'Floor Plan', value: audit.summary.has_floor_plan ? 'Linked' : 'None', color: audit.summary.has_floor_plan ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400' },
                            { label: 'Verified', value: `${audit.summary.verified_assets}/${audit.summary.active_assets}`, color: 'text-violet-600 dark:text-violet-400' },
                          ].map(s => (
                            <div key={s.label} className="bg-muted/50 border border-border/50 rounded-sm p-3 text-center">
                              <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">{s.label}</p>
                              <p className={`font-mono text-lg font-bold mt-1 ${s.color}`}>{s.value}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-4 gap-3 mb-5">
                          {[
                            { label: 'Completeness', value: `${audit.overall_completeness_pct}%`,
                              color: audit.overall_completeness_pct >= 80 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400' },
                            { label: 'Critical Gaps', value: audit.critical_gap_count,
                              color: audit.critical_gap_count === 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400' },
                            { label: 'High-Value Assets', value: audit.high_value_assets, color: 'text-violet-600 dark:text-violet-400' },
                            { label: 'Verified', value: `${audit.verified_assets}/${audit.active_assets}`, color: 'text-cyan-600 dark:text-cyan-400' },
                          ].map(s => (
                            <div key={s.label} className="bg-muted/50 border border-border/50 rounded-sm p-3 text-center">
                              <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">{s.label}</p>
                              <p className={`font-mono text-lg font-bold mt-1 ${s.color}`}>{s.value}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Trade Scorecard (handover audits only) */}
                      {isHandover && audit.trade_checks && Object.keys(audit.trade_checks).length > 0 && (
                        <div className="mb-5" data-testid="trade-scorecard">
                          <h3 className="text-[10px] font-heading uppercase tracking-widest text-emerald-600 dark:text-emerald-500/80 mb-3">Trade Scorecard</h3>
                          <div className="border border-border rounded-sm overflow-hidden">
                            <table className="w-full text-left">
                              <thead>
                                <tr className="bg-muted/30 border-b border-border">
                                  <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Trade</th>
                                  <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Grade</th>
                                  <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Docs</th>
                                  <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Assets</th>
                                  <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Submittals</th>
                                  <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground">RFIs</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-border/50">
                                {Object.entries(audit.trade_checks).map(([key, tc]) => (
                                  <tr key={key} className="hover:bg-muted/10" data-testid={`trade-score-${tc.csi_number}`}>
                                    <td className="px-3 py-2">
                                      <span className="text-xs text-foreground">{tc.csi_number}</span>
                                      <span className="text-[10px] text-muted-foreground ml-1.5">{tc.trade}</span>
                                    </td>
                                    <td className="px-3 py-2">
                                      <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-sm border ${GRADE_COLORS[tc.grade] || GRADE_COLORS.C}`}>
                                        {tc.grade}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-[10px] font-mono text-muted-foreground">{tc.folders}</td>
                                    <td className="px-3 py-2 text-[10px] font-mono text-muted-foreground">{tc.assets}</td>
                                    <td className="px-3 py-2 text-[10px] font-mono text-muted-foreground">{tc.submittals}</td>
                                    <td className="px-3 py-2 text-[10px] font-mono text-muted-foreground">
                                      {tc.open_rfis > 0 ? (
                                        <span className="text-rose-600 dark:text-rose-400">{tc.open_rfis} open</span>
                                      ) : (
                                        <span className="text-emerald-600 dark:text-emerald-400">clear</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* AI Report */}
                      <div className="bg-muted/30 border border-border/50 rounded-sm p-4">
                        <h3 className="text-[10px] font-heading uppercase tracking-widest text-cyan-600 dark:text-cyan-500/80 mb-3">
                          {isHandover ? 'Handover Readiness Report' : 'AI Audit Report'}
                        </h3>
                        <div className="prose prose-invert prose-sm max-w-none text-foreground/80 text-xs leading-relaxed whitespace-pre-wrap font-mono">
                          {audit.ai_report}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
